import React from 'react';
import { X } from 'lucide-react';
import OrgNode from './OrgNode';

const EMPTY_LIST = [];
const EMPTY_SET = new Set();

/**
 * OrgOrphansPanel - the "No reporting chain" drawer: employees with neither
 * a manager nor any reports of their own, surfaced separately from the real
 * forest instead of rendering as one-person trees.
 *
 * ux-fixes-brief.md Task 3 (BLOCKER #2): this used to render inline, to the
 * right of the tree, inside the SAME horizontally-scrolling content
 * (`OrgChartTree.js`'s forest row) - at rest, whenever the tree was wide
 * enough to push the panel past the canvas's visible width, the panel
 * itself was clipped by the canvas edge ("NO REP...", half cards). The
 * preferred fix (a docked sidebar permanently outside the scrolling tree
 * area) was judged too invasive to land safely alongside Task 2's own
 * canvas/zoom changes in the same pass, so this takes the brief's explicit
 * lighter fallback instead: a collapsible drawer, closed by default, opened
 * by a toolbar button ("No reporting chain · N" - `org-orphans-toggle` in
 * OrgChart.js) and rendered here as an absolutely-positioned overlay INSIDE
 * the canvas shell - a sibling of the scrollable canvas, exactly like the
 * zoom pill (`OrgZoomControls`). Its bounds are the shell's own fixed
 * visible size, never the scrollable content's, so it is always either
 * fully open or fully closed - never half off-screen.
 *
 * `data-testid="org-orphans"` / `"org-orphans-count"` and the orphan
 * `OrgNode` cards keep their exact testids from the old inline panel, so
 * anything that already asserted on the panel's CONTENTS only needs to open
 * it first, not learn a new shape.
 */
const OrgOrphansPanel = ({
  orphans = EMPTY_LIST,
  open,
  onClose,
  me,
  highlightedIds = EMPTY_SET,
  dimmedIds = EMPTY_SET,
}) => {
  if (!open || orphans.length === 0) return null;

  return (
    <div
      data-testid="org-orphans"
      className="org-orphans-drawer"
      role="region"
      aria-labelledby="org-orphans-heading"
    >
      <div className="org-orphans__header">
        <h3 id="org-orphans-heading" className="org-orphans__title">No reporting chain</h3>
        <span data-testid="org-orphans-count" className="org-orphans__count">
          {orphans.length}
        </span>
        <button
          type="button"
          data-testid="org-orphans-close"
          aria-label="Close no reporting chain panel"
          onClick={onClose}
          className="org-orphans__close"
        >
          <X size={15} />
        </button>
      </div>
      <div className="org-orphans__grid">
        {orphans.map((node) => (
          <OrgNode
            key={node.id}
            node={node}
            isMe={me != null && node.id === me}
            highlighted={highlightedIds.has(node.id)}
            dimmed={dimmedIds.has(node.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default OrgOrphansPanel;
